import { useMutation } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';

import type { Lead, LeadCreateInput } from '../../api/types';
import { MutationError } from '../../components/MutationError';
import { useProjectApi } from '../../projects/useProjectApi';

// Lead anlegen (Dominik 12.10.2026: Terminierer und Support dürfen das) – z. B. ein neuer Eigentümer, der auf der Firmennummer
// anruft. Nachname oder Firma genügt; Gebiet und Makler ordnet das System über die Adresse zu, Dubletten erkennt es über Nummer
// und Adresse.
export function LeadCreateForm({
  phone = '',
  onCreated,
  onCancel,
}: {
  phone?: string;
  onCreated: (lead: Lead) => void;
  onCancel?: () => void;
}) {
  const papi = useProjectApi();
  const [f, setF] = useState({ first_name: '', last_name: '', company_name: '', phone, mobile_phone: '', email: '', street: '', house_number: '', postal_code: '',
    city: '', notes: '' });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((v) => ({ ...v, [k]: e.target.value }));
  const create = useMutation({ mutationFn: (body: LeadCreateInput) => papi.createLead(body), onSuccess: onCreated });
  const valid = !!(f.last_name.trim() || f.company_name.trim()) && (!f.postal_code || /^[0-9]{5}$/.test(f.postal_code));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const t = (v: string) => v.trim() || undefined;
    create.mutate({
      source: 'MANUAL',
      status: 'NEW',
      first_name: t(f.first_name),
      last_name: t(f.last_name),
      company_name: t(f.company_name),
      phone: t(f.phone),
      mobile_phone: t(f.mobile_phone),
      email: t(f.email),
      street: t(f.street),
      house_number: t(f.house_number),
      postal_code: t(f.postal_code),
      city: t(f.city),
      notes: t(f.notes),
    });
  };
  return (
    <form className="lead-create" onSubmit={submit} aria-label="Lead anlegen">
      <div className="inline-form">
        <label className="field">
          <span>Vorname</span>
          <input value={f.first_name} maxLength={100} onChange={set('first_name')} />
        </label>
        <label className="field">
          <span>Nachname</span>
          <input value={f.last_name} maxLength={100} onChange={set('last_name')} />
        </label>
        <label className="field">
          <span>Firma</span>
          <input value={f.company_name} maxLength={200} onChange={set('company_name')} />
        </label>
      </div>
      <div className="inline-form">
        <label className="field">
          <span>Telefon</span>
          <input value={f.phone} maxLength={30} inputMode="tel" onChange={set('phone')} />
        </label>
        <label className="field">
          <span>Mobil</span>
          <input value={f.mobile_phone} maxLength={30} inputMode="tel" onChange={set('mobile_phone')} />
        </label>
        <label className="field">
          <span>E-Mail</span>
          <input type="email" value={f.email} onChange={set('email')} />
        </label>
      </div>
      <div className="inline-form">
        <label className="field">
          <span>Straße</span>
          <input value={f.street} maxLength={200} onChange={set('street')} />
        </label>
        <label className="field narrow">
          <span>Nr.</span>
          <input value={f.house_number} maxLength={20} onChange={set('house_number')} />
        </label>
        <label className="field narrow">
          <span>PLZ</span>
          <input value={f.postal_code} maxLength={5} inputMode="numeric" onChange={set('postal_code')} />
        </label>
        <label className="field">
          <span>Ort</span>
          <input value={f.city} maxLength={100} onChange={set('city')} />
        </label>
      </div>
      <label className="field wide">
        <span>Notiz (optional)</span>
        <textarea rows={2} maxLength={4000} value={f.notes} onChange={set('notes')} />
      </label>
      {create.isError && <MutationError error={create.error} />}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={!valid || create.isPending}>
          Lead anlegen
        </button>
        {onCancel && (
          <button type="button" className="btn btn-ghost" onClick={onCancel}>
            Abbrechen
          </button>
        )}
        {!valid && <span className="muted small">Nachname oder Firma angeben; PLZ mit 5 Ziffern.</span>}
      </div>
    </form>
  );
}
